/** Keep a long session responsive without throwing away its searchable history. */
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { TranscriptTurn } from '@/api/types';

const PAGE_SIZE = 100;

export const TranscriptPane = memo(function TranscriptPane({
  turns,
  presentation = false,
}: {
  turns: TranscriptTurn[];
  presentation?: boolean;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const [following, setFollowing] = useState(true);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? turns.filter((t) =>
          `${t.speaker || ''} ${t.text}`.toLowerCase().includes(needle),
        )
      : turns;
  }, [turns, query]);
  const visible = matches.slice(-limit);

  useEffect(() => {
    const node = scroller.current;
    if (node && pinned.current && !query) node.scrollTop = node.scrollHeight;
  }, [turns, query]);

  function handleScroll() {
    if (presentation) return;
    const node = scroller.current;
    if (!node) return;
    pinned.current =
      node.scrollHeight - node.scrollTop - node.clientHeight < 48;
    setFollowing(pinned.current);
  }

  return (
    <div className="pane">
      <header className="pane__header">
        <h2 className="pane__title">Transcript</h2>
        <span className="privacy-label">{turns.length} lines</span>
        <input
          type="search"
          className="field__input transcript-search"
          aria-label="Search transcript"
          placeholder="Find a name or something they said…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setLimit(PAGE_SIZE);
          }}
        />
      </header>
      <div className="pane__body" ref={scroller} onScroll={handleScroll}>
        {matches.length > limit ? (
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              pinned.current = false;
              setFollowing(false);
              setLimit((n) => n + PAGE_SIZE);
            }}
          >
            Show earlier lines ({matches.length - limit})
          </button>
        ) : null}
        {turns.length === 0 ? (
          <p className="transcript-empty">
            The conversation will appear here when your microphone is on.
          </p>
        ) : matches.length === 0 ? (
          <p className="transcript-empty">
            No lines match “{query}”. Try a name or a shorter phrase.
          </p>
        ) : (
          visible.map((turn, index) => (
            <p
              className="turn"
              key={turnKey(turn, matches.length - visible.length + index)}
            >
              <span className="turn__speaker">{turn.speaker || 'Speaker'}</span>
              {turn.text}
            </p>
          ))
        )}
      </div>
      <div className="transcript-footer">
        <span role="status">
          {query
            ? `${matches.length} matching lines`
            : following
              ? 'Following the conversation'
              : 'Reading earlier lines'}
        </span>
        {!following && !query ? (
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              pinned.current = true;
              setFollowing(true);
              if (scroller.current)
                scroller.current.scrollTop = scroller.current.scrollHeight;
            }}
          >
            Latest ↓
          </button>
        ) : null}
      </div>
    </div>
  );
});

export function turnKey(turn: TranscriptTurn, index: number): string {
  if (turn.id !== undefined && turn.id !== null) return `id:${turn.id}`;
  return `local:${turn.speaker ?? ''}:${turn.start_ms ?? index}:${turn.text ?? ''}`;
}
